import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faChevronDown,
  faChevronUp,
  faBookOpen,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import womensHealthService, {
  WHContent,
} from '../../services/womensHealthService';
import './WHArticleList.css';

type Variant = 'card' | 'accordion';

interface WHArticleListProps {
  stage: 'puberty' | 'pregnancy' | 'postpartum' | 'menopause';
  contentType: string;
  variant?: Variant;
  icon?: IconDefinition;
  accentColor?: string;
  getAccent?: (row: WHContent) => string | undefined;
  getIcon?: (row: WHContent) => IconDefinition | undefined;
  emptyMessage?: string;
  articles?: WHContent[];
}

const parseLines = (body: string): string[] =>
  body
    .split(/\r?\n+/)
    .map((l) => l.replace(/^\s*[-•*·]\s*/, '').trim())
    .filter(Boolean);

const renderBlocks = (body: string, accentColor: string) => {
  const lines = parseLines(body);
  if (lines.length === 0) return null;
  if (lines.length === 1) {
    return <p className="wh-article-single">{lines[0]}</p>;
  }
  return (
    <ul className="wh-article-bullet-list">
      {lines.map((item, j) => (
        <li key={j} style={{ ['--wh-bullet' as any]: accentColor }}>
          <span className="wh-article-bullet-text">{item}</span>
        </li>
      ))}
    </ul>
  );
};

const WHArticleList: React.FC<WHArticleListProps> = ({
  stage,
  contentType,
  variant = 'card',
  icon = faBookOpen,
  accentColor = '#6366f1',
  getAccent,
  getIcon,
  emptyMessage = 'No content available yet.',
  articles: providedArticles,
}) => {
  const [items, setItems] = useState<WHContent[]>(providedArticles || []);
  const [loading, setLoading] = useState(!providedArticles);
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    if (providedArticles) {
      setItems(providedArticles);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const rows = await womensHealthService.getContent(stage, contentType);
        if (!cancelled) setItems(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error(`Failed to load ${stage}/${contentType}:`, err);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stage, contentType, providedArticles]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [items]
  );

  if (loading) {
    return (
      <div className="wh-article-loading">
        <FontAwesomeIcon icon={faSpinner} spin /> Loading content...
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <div className="wh-article-empty">
        <FontAwesomeIcon icon={icon} />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  if (variant === 'accordion') {
    return (
      <div className="wh-article-accordion-list">
        {sorted.map((row) => {
          const isOpen = openId === row.id;
          const rowAccent = getAccent?.(row) ?? accentColor;
          const rowIcon = getIcon?.(row) ?? icon;
          return (
            <div
              key={row.id}
              className={`wh-article-accordion-item${isOpen ? ' open' : ''}`}
            >
              <button
                className="wh-article-accordion-trigger"
                onClick={() => setOpenId(isOpen ? null : row.id)}
                aria-expanded={isOpen}
              >
                <div
                  className="wh-article-accordion-icon"
                  style={{ background: `${rowAccent}18`, color: rowAccent }}
                >
                  <FontAwesomeIcon icon={rowIcon} />
                </div>
                <span className="wh-article-accordion-title">{row.title}</span>
                <FontAwesomeIcon
                  icon={isOpen ? faChevronUp : faChevronDown}
                  className="wh-article-accordion-chevron"
                />
              </button>
              {isOpen && (
                <div className="wh-article-accordion-content">
                  {renderBlocks(row.content, rowAccent)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="wh-article-card-grid">
      {sorted.map((row) => {
        const rowAccent = getAccent?.(row) ?? accentColor;
        const rowIcon = getIcon?.(row) ?? icon;
        return (
          <article
            key={row.id}
            className="wh-article-card"
            style={
              {
                ['--wh-accent' as any]: rowAccent,
                ['--wh-accent-soft' as any]: `${rowAccent}18`,
                ['--wh-accent-edge' as any]: `${rowAccent}40`,
              } as React.CSSProperties
            }
          >
            <div className="wh-article-card-accent" />
            <div className="wh-article-card-head">
              <div className="wh-article-card-icon">
                <FontAwesomeIcon icon={rowIcon} />
              </div>
              <h3 className="wh-article-card-title">{row.title}</h3>
            </div>
            <div className="wh-article-card-body">
              {renderBlocks(row.content, rowAccent)}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default WHArticleList;
